import { isTouchDevice, getGlobal } from "../compat";
import { isOffline, getEnv } from "../file-loaders/net/endpoints";
import { analytics } from '../analytics/index';
import { LogLevels } from "./Logger";

const global = getGlobal();
const _window = global;

export class DefaultLogger {
  constructor() {
    this.runtimeStats = {};
    this.level = -1;
    this.setLevel(LogLevels.ERROR);
    this._reportError = this._reportError.bind(this);
  }

  /**
   * Initialize Logger object with options.
   *
   * @param {object}   [options] - Options object to configure the Logger.
   * @param {function} [options.eventCallback] - An optional callback used for processing the log entry with properties like "category", "timestamp", etc.
   It can be used as for analytics tracking by filtering and listening to specific category users are interested.
   The expected argument is the user supplied entry object instrumented with "timestamp" and "sessionId" properties.
   * @param {string}   [options.sessionId] - An optional id for each browser session. Default gets generated based on current time stamp if not specified.
   * @param {number}   [options.logLevel] - An optional level to define the log level. Default is LogLevels.ERROR if not specified.
   *
   */
  initialize(options) {
    if (options.eventCallback)
    this.callback = options.eventCallback;

    this.sessionId = options.sessionId;
    if (!this.sessionId) {
      var now = Date.now() + "";
      this.sessionId = parseFloat((Math.random() * 10000 | 0) + "" + now.substring(4));
    }

    if (typeof options.logLevel === 'number') {
      this.setLevel(options.logLevel);
    }

    this.environmentInfo = {
      touch: isTouchDevice(),
      env: getEnv(),
      referer: getReferer(),
      version: global.LMV_VIEWER_VERSION,
      build_type: global.LMV_BUILD_TYPE
    };

    var startEvent = {
      category: "viewer_start",
      touch: this.environmentInfo.touch,
      env: this.environmentInfo.env,
      referer: this.environmentInfo.referer,
      version: this.environmentInfo.version,
      build_type: this.environmentInfo.build_type
    };
    this.track(startEvent);

    var _this = this;
    this.interval = setInterval(function () {
      _this.reportRuntimeStats();
    }, 60000);
  }

  /**
   * Stop the runtime stats reporting every min.
   */
  shutdown() {
    clearInterval(this.interval);
    this.interval = undefined;
  }

  /**
   * Track the user inputted entry by appending additional info "timestamp", "sessionId"
   * @param {object} [entry] - User object to define the track object,
   e.g. {
   category : "load_document",
   urn: "xyz"
   }
   */
  track(entry) {
    this.updateRuntimeStats(entry);

    if (isOffline() || !this.sessionId) {
      return;
    }

    if (this.callback) {
      entry.timestamp = Date.now();
      entry.sessionId = this.sessionId;

      this.callback(entry);
    }

    if (entry?.category === 'error') {
      trackError('viewer.error.tracked', entry);
    }
  }

  updateRuntimeStats(entry) {
    if (Object.prototype.hasOwnProperty.call(entry, 'aggregate')) {
      switch (entry.aggregate) {
        case 'count':
          if (this.runtimeStats[entry.name] > 0) {
            this.runtimeStats[entry.name]++;
          } else {
            this.runtimeStats[entry.name] = 1;
          }
          this.runtimeStats._nonempty = true;
          break;
        case 'last':
          this.runtimeStats[entry.name] = entry.value;
          this.runtimeStats._nonempty = true;
          break;
        default:
          this.warn('unknown log aggregate type');
      }
    }
  }

  reportRuntimeStats() {
    if (this.runtimeStats._nonempty) {
      delete this.runtimeStats._nonempty;

      this.runtimeStats.category = 'misc_stats';
      this.track(this.runtimeStats);
      this.runtimeStats = {};
    }
  }

  setLevel(level) {
    if (this.level === level)
    return;

    this.level = level;
  }

  error(...args) {
    if (this.level >= LogLevels.ERROR) {
      this._reportError(...args);
    }
  }

  warn(...args) {
    if (this.level >= LogLevels.WARNING) {
      console.warn(...args);
    }
  }

  info(...args) {
    if (this.level >= LogLevels.INFO) {
      console.info(...args);
    }
  }

  log(...args) {
    if (this.level >= LogLevels.LOG) {
      console.log(...args);
    }
  }

  debug(...args) {
    if (this.level >= LogLevels.DEBUG) {
      console.log(...args);
    }
  }

  /**
   * Reports an error to the browser console and to the logger's callback.
   * Invoked by developers when method `logger.error()` is used.
   * Forwards the arguments directly into `console.error()`.
   * @private
   */
  _reportError() {
    console.error.apply(console, arguments);

    const msg = Array.prototype.slice.call(arguments).join(' ');
    if (this.callback) {
      this.callback({ category: 'error', message: msg });
    }

    trackError('viewer.error.logged', msg);
  }
}

function trackError(name, content) {
  let value;
  if (typeof content === 'string') {
    value = {
      message: content
    };
  } else if (typeof content === 'object') {
    value = content;
  }
  analytics.track(name, value);
}

function getReferer() {
  if (typeof window !== 'undefined') {
    return encodeURI(_window.location.href);
  }
  return '';
}